import { useQuery } from "@tanstack/react-query";

import { api } from "@utils/api";

import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { Pagination } from "@customTypes/index";
import { Laboratory } from "@customTypes/laboratory";

import { labQKeys } from "./queryKeys";


const select = (res: any) => {
  // console.log(res.data);
  const pagination: Partial<Pagination> = {
    page: res.data.pagination.page,
    pageSize: res.data.pagination.page_size,
    pageCount: res.data.pagination.page_count,
    total: res.data.pagination.total,
  };

  if (res.data.data.length) {
    const newData: Laboratory[] = [];

    for (const i in res.data.data) {
      const {
        _id,
        lab_type,
        lab_type_id,
        lab_group,
        reason,
        ordered_by,
        status,
        date,
        fast_consultation,
        patient_id,
        consultation_id,
        consultation_info,
        files,
        aid,
        updated_at,
        bill_status,
      } = res.data.data[i];

      if (fast_consultation && consultation_info) {
        newData.push({
          id: _id,
          consultationId: consultation_id,
          consultationInfo: {
            consultationType: consultation_info?.consultation_type,
            physician: consultation_info?.physician,
            cId: consultation_info?.c_id,
            date: consultation_info?.date
              ? new Date(consultation_info.date)
              : undefined,
          },
          patientId: patient_id,
          date: new Date(date),
          isFastConsultation: fast_consultation,
          files,
          reason,
          supportStaff: {
            id: aid?._id,
            fullName: aid?.full_name,
          },
          labType: "N/A",
          orderedBy: {
            fullName: ordered_by?.full_name,
            id: ordered_by?._id,
          },
          status,
          updatedAt: new Date(updated_at),
          billStatus: bill_status,
        });
      } else {
        newData.push({
          id: _id,
          consultationId: consultation_id,
          patientId: patient_id,
          date: new Date(date),
          isFastConsultation: fast_consultation,
          consultationInfo: consultation_info
            ? {
                consultationType: consultation_info.consultation_type,
                physician: consultation_info.physician,
                cId: consultation_info.c_id,
                date: consultation_info.date
                  ? new Date(consultation_info.date)
                  : undefined,
              }
            : undefined,
          files,
          reason,
          labType: lab_type,
          labTypeId: lab_type_id,
          labGroupId: lab_group,
          orderedBy: {
            fullName: ordered_by?.full_name,
            id: ordered_by?._id,
          },
          supportStaff: {
            id: aid?._id,
            fullName: aid?.full_name,
          },
          status,
          updatedAt: new Date(updated_at),
          billStatus: bill_status,
        });
      }
    }

    // console.log({ newData, pagination });
    return {
      newData,
      pagination,
    };
  }
  return null;
};

interface QueryProps {
  patientId: string;
  withFilesOnly?: boolean;
  fastConsultationOnly?: boolean;
  consultationId?: string;
  isEnabled?: boolean;
  pagination?: Partial<Pagination>;
  search?: string;
  status?: string;
  category?: string;
}

export const useGetLabsForPatient = ({
  patientId,
  fastConsultationOnly,
  consultationId,
  pagination,
  status,
  category,
  withFilesOnly = false,
  isEnabled = true,
  search = "",
}: QueryProps) => {
  const handleErrors = useQueryErrorHandling();

  return useQuery({
    queryKey: labQKeys.patientWithFilters(patientId, {
      withFilesOnly,
      fastConsultationOnly,
      consultationId,
      pagination,
      ...(search && { search }),
      ...(category && { category }),
      status,
    }),
    queryFn: () => {
      return api.get(`lab/patient/${patientId}`, {
        params: {
          only_with_files: withFilesOnly,
          ...(fastConsultationOnly !== undefined && {
            fast_consultation: fastConsultationOnly,
          }),
          ...(consultationId !== undefined && {
            consultation_id: consultationId,
          }),
          ...(search && { search }),
          ...(status && { status }),
          ...(category && { category }),
          ...(pagination && {
            page: pagination.page,
            page_size: pagination.pageSize,
          }),
        },
      });
    },
    select,
    enabled: isEnabled,
    staleTime: Infinity,

    onError: (res: any) => {
      console.error("[From Get Labs for Patient]", res);
      handleErrors(res, "query");
    },
  });
};
